import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { bumpCompanyScopeVersion, setSelectedCompany, setSelectedStore } from "@/store/appSlice";
import { refreshSessionInit } from "@/store/sessionSlice";
import { invalidate } from "@/lib/query/invalidate";
import { notify } from "@/core/toast/notify";
import { clearPosSessionStorage } from "@/utils/posSessionStorage";
import { cn } from "@/utils/cn";
import { logout } from "@/store/authSlice";

/**
 * Full-screen gate shown after login when the user can access more than one company and has not
 * yet chosen one. Enforces the mandatory active-company rule on the client: no scoped screen
 * renders until a company is selected.
 */
export function CompanySelectionScreen() {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const qc = useQueryClient();
  const companies = useAppSelector((s) => s.session.companies);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const hasCompanies = companies.length > 0;

  const onContinue = async () => {
    const company = companies.find((c) => c.id === selectedId);
    if (!company || submitting) return;
    setSubmitting(true);
    try {
      await dispatch(refreshSessionInit(company.id)).unwrap();
      clearPosSessionStorage();
      dispatch(setSelectedStore(null));
      dispatch(setSelectedCompany(company));
      invalidate.companyScope(qc, company.id);
      dispatch(bumpCompanyScopeVersion());
      notify.t.success("workingIn", { name: company.name });
    } catch {
      notify.t.error("companySwitchFailed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        {hasCompanies ? (
          <>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("company.chooseTitle")}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("company.chooseSubtitle")}
            </p>

            <ul className="mt-5 max-h-80 space-y-2 overflow-y-auto">
              {companies.map((c) => {
                const active = c.id === selectedId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition",
                        active
                          ? "border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-950/30"
                          : "border-slate-200 hover:border-primary-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800",
                      )}
                      aria-pressed={active}
                    >
                      <span>
                        <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                          {c.name}
                        </span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">{c.code}</span>
                      </span>
                      <span
                        className={cn(
                          "h-4 w-4 rounded-full border-2",
                          active ? "border-primary-500 bg-primary-500" : "border-slate-300 dark:border-slate-600",
                        )}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>

            <Button
              className="mt-6 w-full"
              disabled={selectedId == null}
              loading={submitting}
              onClick={onContinue}
            >
              {t("company.continue")}
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("company.noAccessTitle")}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("company.noAccessSubtitle")}
            </p>
            <Button
              className="mt-6 w-full"
              variant="secondary"
              onClick={() => void dispatch(logout())}
            >
              {t("profileMenu.signOut")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
